import { MenuClose, PersonSearch } from '@fs/zion-icon/dist/cjs/icons'
import { css } from '@emotion/core'
import { useHistory, useLocation } from '@fs/zion-router'
import { usePageView } from '@fs/zion-analytics'
import { Checkbox, ResponsiveTableRow, Row, TextField, useOverlay, H6, Button } from '@fs/zion-ui'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { getUserProfiles } from '../../../services/userService'
import { getIdElement } from '../../../util/StringUtil'
import UserProfileSearchFiltersButtonAndOverlay from './UserProfileSearchFiltersButtonAndOverlay'

const wrapit = css`
  overflow-wrap: break-word;
`

export default function UserProfileSearch() {
  usePageView({ page_detail: 'DMConsole:UserProfileSearch' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.search.userProfile'))
  const filtersOverlay = useOverlay()
  const history = useHistory()
  const location = useLocation()
  const { error } = useNotifications()
  const [searchByContactID, setSearchByContactID] = useState(false)
  const [searchValue, setSearchValue] = useState()
  const [country, setCountry] = useState()
  const [countryId, setCountryId] = useState()
  const [stateOrProvince, setStateOrProvince] = useState()
  const [stateOrProvinceId, setStateOrProvinceId] = useState()
  const [userProfiles, setUserProfiles] = useState()
  const searchBtnRef = useRef()
  const searchBtnDisabled = !searchValue || searchValue.length < 2
  const clearBtnDisabled = !(searchValue || country || stateOrProvince)

  const handleUserProfileSearch = useCallback(
    async (contactId, profileName, cId, sOrPId) => {
      try {
        setUserProfiles()
        const userProfilesResponse = await getUserProfiles(contactId, profileName, cId, sOrPId)
        setUserProfiles(userProfilesResponse.data)
      } catch (err) {
        error(
          t('search.errors.retrievingUserProfiles', {
            defaultValue: `Error retrieving user profiles: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      }
    },
    [error, t]
  )

  useEffect(() => {
    if (!filtersOverlay.isOpen && !filtersOverlay.transitioning) {
      searchBtnRef.current.focus()
    }
  }, [filtersOverlay.isOpen, filtersOverlay.transitioning])

  useEffect(() => {
    if (location.search && location.search !== '') {
      const searchParams = new URLSearchParams(location.search)
      const contactId = searchParams.get('contactId')
      const profileName = searchParams.get('profileName')
      if (contactId) {
        setSearchValue(contactId)
        setSearchByContactID(true)
      } else {
        setSearchValue(profileName)
      }
      const cId = searchParams.get('countryId')
      if (cId) {
        setCountryId(cId)
      }
      const sOrPId = searchParams.get('stateOrProvinceId')
      if (sOrPId) {
        setStateOrProvinceId(sOrPId)
      }
      if (contactId || profileName) {
        handleUserProfileSearch(contactId, profileName, cId, sOrPId)
      }
    }
  }, [handleUserProfileSearch, location.search])

  const onFilterChange = (filter, value) => {
    switch (filter) {
      case 'country': {
        setCountry(value)
        if (value.standard && value.standard.placeType === 'Country') {
          setCountryId(value.standard.id)
        } else {
          setCountryId()
        }
        break
      }
      case 'stateOrProvince': {
        setStateOrProvince(value)
        if (value.standard && (value.standard.placeType === 'State' || value.standard.placeType === 'Province')) {
          setStateOrProvinceId(value.standard.id)
        } else {
          setStateOrProvinceId()
        }
        break
      }
      default: {
        break
      }
    }
  }

  const onUserProfileClick = (cisId) => {
    history.push(`/user/${getIdElement(cisId)}/summary?context=cisUserId`)
  }

  const clearBtnOnClick = () => {
    setCountry()
    setCountryId()
    setStateOrProvince()
    setStateOrProvinceId()
    setSearchValue()
    setSearchByContactID(false)
    setUserProfiles()
    history.push(`/search/userProfile`)
  }

  const searchBtnOnClickHandler = useCallback(async () => {
    if (!searchBtnDisabled) {
      let searchParams = ''
      if (searchByContactID) {
        searchParams += `contactId=${searchValue}`
      } else {
        searchParams += `profileName=${searchValue}`
      }
      if (countryId) {
        searchParams += `&countryId=${countryId}`
      }
      if (stateOrProvinceId) {
        searchParams += `&stateOrProvinceId=${stateOrProvinceId}`
      }
      history.push(`/search/userProfile?${searchParams}`)
    }
  }, [countryId, history, searchBtnDisabled, searchByContactID, searchValue, stateOrProvinceId])

  const getLocationString = (c, sp) => {
    let locationString = ''
    if (sp && c) {
      locationString = `${sp}, ${c}`
    }
    if (sp && !c) {
      locationString = sp
    }
    if (!sp && c) {
      locationString = c
    }
    return locationString
  }

  return (
    <>
      <Row>
        <TextField
          label={searchByContactID ? t('user.userProfile.contactId', 'Contact ID') : t('profileName', 'Profile Name')}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          type="text"
          onEnterKey={searchBtnOnClickHandler}
        />
        <Checkbox
          name="searchByContactID"
          label={t('user.userProfile.searchByContactId', 'Search by Contact ID')}
          checked={searchByContactID}
          onChange={(e) => setSearchByContactID(e.target.checked)}
          alignY="nextToInput"
        />
      </Row>
      <Row>
        <Button
          Icon={PersonSearch}
          onClick={searchBtnOnClickHandler}
          emphasis="high"
          disabled={searchBtnDisabled}
          ref={(element) => {
            searchBtnRef.current = element
          }}
        >
          {t('action.search', 'Search')}
        </Button>
        <UserProfileSearchFiltersButtonAndOverlay
          country={country}
          stateOrProvince={stateOrProvince}
          onFilterChange={onFilterChange}
          {...filtersOverlay}
        />
        <Button Icon={MenuClose} onClick={clearBtnOnClick} emphasis="medium" disabled={clearBtnDisabled}>
          {t('action.clear', 'Clear')}
        </Button>
      </Row>
      {userProfiles && (
        <>
          <Row alignX="end">
            <H6>
              {t('user.userProfile.resultCount', `${userProfiles.length} results`, {
                count: userProfiles.length,
              })}
            </H6>
          </Row>
          <DmcResponsiveTable
            atSizeColumns={{
              default: {
                labels: [
                  t('cisId', 'CIS ID'),
                  t('user.userProfile.contactId', 'Contact ID'),
                  t('profileName', 'Profile Name'),
                  t('location', 'Location'),
                  t('email', 'Email'),
                ],
                columnWidths: [2, 2, 3, 3, 2],
              },
              xs: {
                labels: [
                  t('user.userProfile.cisIdContactIdProfileName', 'CIS ID / Contact ID / Profile Name'),
                  t('user.userProfile.emailLocation', 'Location / Email'),
                ],
                combinationRules: [
                  ['column1', 'column2', 'column3'],
                  ['column4', 'column5', 'column6'],
                ],
              },
              sm: {
                labels: [
                  t('user.userProfile.cisIdContactIdProfileName', 'CIS ID / Contact ID / Profile Name'),
                  t('user.userProfile.emailLocation', 'Location / Email'),
                ],
                combinationRules: [
                  ['column1', 'column2', 'column3'],
                  ['column4', 'column5', 'column6'],
                ],
              },
              md: {
                labels: [
                  t('user.userProfile.cisIdContactId', 'CIS ID & Contact ID'),
                  t('user.userProfile.profileNameLocation', 'Profile Name & Location'),
                  t('email', 'Email'),
                ],
                combinationRules: [['column1', 'column2'], ['column3', 'column4'], ['column5']],
              },
              xl: {
                labels: [
                  t('cisId', 'CIS ID'),
                  t('user.userProfile.contactId', 'Contact ID'),
                  t('profileName', 'Profile Name'),
                  t('location', 'Location'),
                  t('email', 'Email'),
                ],
                columnWidths: [2, 2, 3, 2, 3],
              },
            }}
            totalColumnCount={6}
            separator={<br />}
          >
            {userProfiles.map((userProfile) => {
              return (
                <ResponsiveTableRow
                  key={userProfile.cisId}
                  column1={getIdElement(userProfile.cisId)}
                  column2={<span css={wrapit}>{userProfile.contactId}</span>}
                  column3={<span css={wrapit}>{userProfile.profileName}</span>}
                  column4={getLocationString(userProfile.country, userProfile.stateOrProvince)}
                  column5={<span css={wrapit}>{userProfile.email}</span>}
                  onClick={() => onUserProfileClick(userProfile.cisId)}
                />
              )
            })}
          </DmcResponsiveTable>
        </>
      )}
    </>
  )
}
